import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navItems = [
  { key: 'nav.home', path: '/', icon: 'ri-home-4-line' },
  { key: 'nav.menu', path: '/menu', icon: 'ri-restaurant-2-line' },
  { key: 'nav.scan', path: '/scan', icon: 'ri-qr-scan-line' },
  { key: 'nav.about', path: '/about', icon: 'ri-information-line' },
  { key: 'nav.locations', path: '/locations', icon: 'ri-map-pin-2-line' },
  { key: 'nav.contact', path: '/contact', icon: 'ri-mail-line' },
  { key: 'nav.privacy', path: '/privacy', icon: 'ri-shield-user-line' },
];

function SidebarContent({
  onClose,
  collapsed,
  onToggleCollapse,
}: {
  onClose?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = !!onClose;
  const isCollapsed = collapsed && !isMobile;

  const handleNavClick = (path: string) => {
    if (path.startsWith('/#')) {
      const hash = path.replace('/#', '');
      if (location.pathname === '/') {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    } else {
      navigate(path);
    }
    onClose?.();
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path;
  };

  return (
    <>
      {/* Logo */}
      <div className={`p-4 border-b border-foreground-800/60 flex items-center gap-2 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg flex-shrink-0">
            <i className="ri-restaurant-2-line text-lg" />
          </span>
          {!isCollapsed && (
            <span className="font-heading text-base font-bold text-background-50 tracking-wide">
              BIOLA
            </span>
          )}
        </div>
        {isMobile && onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-background-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        )}
        {!isMobile && onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <i className={isCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="p-3 space-y-0.5">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.key}
              onClick={() => handleNavClick(item.path)}
              className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                active
                  ? 'bg-primary-500 text-background-50 shadow-sm'
                  : 'text-foreground-300 hover:text-background-50 hover:bg-foreground-800/80'
              } ${isCollapsed ? 'justify-center px-2' : ''}`}
            >
              {active && !isCollapsed && (
                <span className="absolute start-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent-300 rounded-full" />
              )}
              <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${active ? 'text-background-50' : ''}`}>
                <i className={item.icon} />
              </span>
              {!isCollapsed && <span>{t(item.key)}</span>}
            </button>
          );
        })}
      </nav>

      {/* Language switcher */}
      <div className="mt-auto p-4 border-t border-foreground-800/60">
        {isCollapsed ? (
          <div className="flex justify-center">
            <button
              onClick={onToggleCollapse}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer"
              title="Expand sidebar"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        ) : (
          <LanguageSwitcher variant="dark" className="w-full text-center" />
        )}
      </div>

      {/* Version info */}
      {!isCollapsed && (
        <div className="px-4 pb-3 text-center">
          <p className="text-[10px] text-foreground-600">App Version: 1.0.0</p>
          <p className="text-[10px] text-foreground-600">Powered by BIOLA</p>
        </div>
      )}
    </>
  );
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      {/* Desktop persistent sidebar */}
      <aside
        className={`hidden lg:flex flex-col h-screen bg-foreground-950 text-background-50 flex-shrink-0 sticky top-0 overflow-hidden transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        <SidebarContent
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(!collapsed)}
        />
      </aside>

      {/* Mobile drawer overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-all ${
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-foreground-950/50 backdrop-blur-sm transition-opacity duration-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={onClose}
        />
        {/* Drawer panel */}
        <div
          className={`absolute top-0 bottom-0 start-0 w-64 bg-foreground-950 text-background-50 flex flex-col overflow-hidden transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full'
          }`}
        >
          <SidebarContent onClose={onClose} />
        </div>
      </div>
    </>
  );
}